// shared/src/config/strings.ts

const pluralDigit = (n: number) => n === 1 ? 'digit' : 'digits'

export const strings = {
  menu: {
    title: 'CRACKPOINT',
    startButton: 'Start Breach',
    leaderboardButton: 'Leaderboard',
    settingsButton: 'Settings',
    aboutButton: 'About',
    closeButton: 'Close',
  },

  about: {
    title: (productFullName: string) => `About ${productFullName}`,
    description: 'CRACKPOINT is a number puzzle roguelike with hint rarity mechanics. Deduce the hidden code using scored hints, each classified by rarity and elimination power.',
    howToPlayTitle: 'How to play:',
    howToPlay: {
      interceptSignals: 'Intercept signals (hints) to learn about the secret code',
      submitCodes: 'Submit access codes (guesses) to breach terminals',
      clearRooms: 'Clear rooms to advance deeper into the facility',
      rarerSignals: 'Rarer signals eliminate more possibilities',
      watchLives: 'Watch your lives — wrong codes cost you',
    },
    versionLabel: (productName: string, version: string) => `Version ${version} — ${productName}`,
  },

  game: {
    loading: 'Loading room...',
    roomLabel: (roomNumber: number) => `Room ${roomNumber}`,
    remainingLabel: (count: number | string) => `${count} remaining`,
    calculating: '...',
    exitButton: 'Exit',
    reserveButton: (remaining: number) => `Reserve (${remaining})`,
    reserveToast: 'Reserve signal decoded',
    powerUpToast: (type: string) => `${type.toUpperCase()} activated`,
  },

  settings: {
    title: 'Settings',
    backButton: '← Back',
    sections: {
      appearance: 'Appearance',
      audio: 'Audio',
      gameplay: 'Gameplay',
      leaderboard: 'Leaderboard',
    },
    labels: {
      theme: 'Theme',
      themeDescription: 'Visual palette',
      crtEffects: 'CRT Effects',
      crtEffectsDescription: 'Scanlines and vignette overlay',
      highContrast: 'High Contrast',
      highContrastDescription: 'Increase contrast for better visibility',
      largeText: 'Large Text',
      largeTextDescription: 'Increase text size',
      reducedMotion: 'Reduced Motion',
      reducedMotionDescription: 'Minimise animations',
      soundEffects: 'Sound Effects',
      soundEffectsDescription: 'Enable game sounds',
      musicVolume: 'Music Volume',
      musicVolumeDescription: 'Background music volume',
      sfxVolume: 'SFX Volume',
      sfxVolumeDescription: 'Sound effects volume',
      hapticFeedback: 'Haptic Feedback',
      hapticFeedbackDescription: 'Vibrate on interactions',
      showTimer: 'Show Timer',
      showTimerDescription: 'Display game timer during play',
      showPercentile: 'Show Percentile',
      showPercentileDescription: 'Display hint rarity percentiles',
      autoDrawHints: 'Auto-Draw Hints',
      autoDrawHintsDescription: 'Automatically draw hints when available',
      defaultCodeLength: 'Default Code Length',
      defaultCodeLengthDescription: 'Starting puzzle difficulty',
      submitScores: 'Submit Scores',
      submitScoresDescription: 'Allow scores on public leaderboard',
      displayName: 'Display Name',
      displayNameDescription: 'Name shown on leaderboard',
      displayNamePlaceholder: 'Enter name...',
    },
    themes: {
      crtDark: 'CRT Dark',
      crtLight: 'CRT Light',
      cleanDark: 'Clean Dark',
      cleanLight: 'Clean Light',
      highContrast: 'High Contrast',
    },
    codeLengths: {
      three: '3 Digits',
      four: '4 Digits',
      five: '5 Digits',
    },
    resetButton: 'Reset All Settings',
    resetConfirm: 'Reset all settings to defaults?',
  },

  gameOver: {
    icon: '🔒',
    title: 'System Lockout',
    description: 'All lives depleted. Connection terminated.',
    statsTitle: 'Final Statistics',
    roomsCleared: 'Rooms Cleared:',
    totalStars: 'Total Stars:',
    bestStreak: 'Best Streak:',
    deepestRoom: 'Deepest Room:',
    reconnectButton: 'Reconnect',
    enterHint: 'Press Enter to return',
  },

  roomComplete: {
    title: 'Room Cleared!',
    codeLabel: 'Code:',
    optimalHintsLabel: 'Optimal Hints:',
    yourAttemptsLabel: 'Your Attempts:',
    devSolutionLabel: 'Show Dev Solution',
    devSolutionPlaceholder: 'Optimal hint sequence would be displayed here',
    lootTitle: 'Loot Found!',
    lootIcon: '🎁',
    powerUpMessage: (type: string) => `${type.toUpperCase()} power-up added to inventory`,
    bonusLivesTitle: 'Bonus Lives!',
    bonusLivesIcon: '❤️',
    bonusLivesMessage: (count: number) => `+${count} life restored`,
    nextRoomButton: 'Next Room',
    enterHint: 'Press Enter to continue',
  },

  hints: {
    title: 'Hints',
    drawnRemaining: (drawn: number, remaining: number) => `${drawn} drawn, ${remaining} remaining`,
    drawButton: 'Draw Hint',
    emptyMessage: 'No hints drawn yet. Draw a hint to begin.',
    compactCount: (drawn: number, total: number) => `${drawn} / ${total} hints`,
    newBadge: 'NEW',
    percentile: (p: number) => `Top ${p}%`,
    nothingCorrect: 'Nothing is correct',
    digitsPlaced: (n: number) => `${n} correct ${pluralDigit(n)} in place`,
    digitsMisplaced: (n: number) => `${n} correct ${pluralDigit(n)} misplaced`,
    placedAndMisplaced: (placed: number, misplaced: number) =>
      `${placed} correct ${pluralDigit(placed)} in place, ${misplaced} correct ${pluralDigit(misplaced)} misplaced`,
    rarity: {
      common: 'Common',
      uncommon: 'Uncommon',
      rare: 'Rare',
      epic: 'Epic',
      legendary: 'Legendary',
    },
    powerUps: {
      boost: 'Boost',
      surge_i: 'Surge I',
      surge_ii: 'Surge II',
      oracle: 'Oracle',
    },
    powerUpIcons: {
      boost: '⚡',
      surge_i: '🔥',
      surge_ii: '💥',
      oracle: '👁️',
    },
  },

  roomMessages: {
    start: ['SYSTEM INITIALISED...', 'CODE LOCK ENGAGED...', 'SECURITY PROTOCOL ACTIVE...'],
    solve: ['ACCESS GRANTED', 'LOCK BYPASSED', 'CODE ACCEPTED'],
    gameOver: ['CONNECTION LOST', 'SESSION TERMINATED', 'ACCESS DENIED'],
  },

  modal: {
    confirmDefault: 'Confirm',
    cancelDefault: 'Cancel',
  },

  errors: {
    genericTitle: 'Something went wrong',
    genericDescription: 'The application encountered an unexpected error. Please refresh to try again.',
    refreshButton: 'Refresh',
  },
} as const
